// Site traffic for the Analytics page (rxk admin / ERP / Admin Hub):
// which page, from where (Facebook, Instagram, Google, a /go/ link…), on
// which device. No personal data — a random id per browser only. The same
// file exists in rxk, Pick-k, medi-k and thekseoul (only SITE_KEY and the
// path rules differ); keep them in step.
import { supabase } from '@/lib/supabaseClient';

export const SITE_KEY = 'rxk';
const SESSION_KEY = 'rxk_traffic_session_id';
// Shared across our sites' own origins only by name; each site keeps its own copy.
const ATTR_KEY = 'thekseoul_attribution';
const ATTR_DAYS = 30;

export function getSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'no-storage';
  }
}

const ua = () => (typeof navigator === 'undefined' ? '' : navigator.userAgent);
export const isBot = () => /bot|crawl|spider|slurp|preview|facebookexternalhit|lighthouse|headless/i.test(ua());
// In-app browsers often send no referrer, so their user agent is the only clue.
export function inAppBrowser(): string | null {
  const u = ua();
  if (/Instagram/i.test(u)) return 'instagram';
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(u)) return 'facebook';
  if (/musical_ly|Bytedance|TikTok/i.test(u)) return 'tiktok';
  return null;
}
export function deviceType(): string {
  const u = ua();
  if (/iPad|Tablet/i.test(u)) return 'Tablet';
  return /Mobi|Android|iPhone/i.test(u) ? 'Mobile' : 'Desktop';
}

// Same rules as the DB's traffic_classify_source(), so an order's source
// matches the visit's.
export function classifySource(referrer: string | null, utmSource: string | null, inApp: string | null): string {
  const s = (utmSource ?? '').trim().toLowerCase();
  const ref = (referrer ?? '').toLowerCase();
  // A link shared on both Facebook and Instagram: let the app / referrer decide.
  if (s === 'social') {
    if (inApp === 'instagram' || /instagram\.com/.test(ref)) return 'Instagram';
    if (inApp === 'facebook' || /facebook\.com|fb\.me|fb\.com/.test(ref)) return 'Facebook';
    return 'Facebook / Instagram';
  }
  if (s) {
    if (/^(fb|facebook|meta|messenger)/.test(s)) return 'Facebook';
    if (/^(ig|insta)/.test(s)) return 'Instagram';
    if (/^google/.test(s)) return 'Google';
    if (/^(wa|whatsapp)/.test(s)) return 'WhatsApp';
    if (/^(tt|tiktok)/.test(s)) return 'TikTok';
    if (/^(yt|youtube)/.test(s)) return 'YouTube';
    if (/^(email|mail|newsletter)/.test(s)) return 'Email';
    if (/^qr/.test(s)) return 'QR code';
    if (/^sms/.test(s)) return 'SMS';
    return s.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (inApp === 'instagram') return 'Instagram';
  if (inApp === 'facebook') return 'Facebook';
  if (inApp === 'tiktok') return 'TikTok';
  const r = (referrer ?? '').toLowerCase();
  if (!r) return 'Direct';
  if (/facebook\.com|fb\.me|fb\.com/.test(r)) return 'Facebook';
  if (/instagram\.com/.test(r)) return 'Instagram';
  if (/^https?:\/\/([a-z0-9-]+\.)*google\.|googlequicksearchbox/.test(r)) return 'Google';
  if (/whatsapp|wa\.me/.test(r)) return 'WhatsApp';
  if (/tiktok\.com/.test(r)) return 'TikTok';
  if (/youtube\.com|youtu\.be/.test(r)) return 'YouTube';
  if (/thekseoul\.(com|xyz)/.test(r)) return 'Our other sites';
  return 'Other sites';
}

export interface Attribution {
  source: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  link_code?: string;
  landing_path: string;
  session_id: string;
  site_key: string;
  at: string;
}

/** Where this visitor last came from (kept 30 days) — sent with orders. */
export function getAttribution(): Attribution | null {
  try {
    const a = JSON.parse(localStorage.getItem(ATTR_KEY) || 'null') as Attribution | null;
    if (!a || Date.now() - new Date(a.at).getTime() > ATTR_DAYS * 86400000) return null;
    return a;
  } catch {
    return null;
  }
}

// Staff devices are never counted. A browser becomes one when staff open
// the admin on it (markStaffDevice) or when ?staff=1 is opened once —
// IP matching alone misses iCloud Private Relay, mobile data, etc.
// ?staff=0 clears it.
const STAFF_KEY = 'thekseoul_staff_device';
export function markStaffDevice(): void {
  try {
    localStorage.setItem(STAFF_KEY, '1');
  } catch {
    /* storage blocked */
  }
}
function isStaffDevice(search: string): boolean {
  try {
    const flag = new URLSearchParams(search).get('staff');
    if (flag === '1') localStorage.setItem(STAFF_KEY, '1');
    if (flag === '0') localStorage.removeItem(STAFF_KEY);
    return localStorage.getItem(STAFF_KEY) === '1';
  } catch {
    return false;
  }
}

export function trackPageView(pathname: string, search: string, landing: boolean): void {
  if (/^\/(admin|my|reseller|go)(\/|$)/.test(pathname) || isBot() || isStaffDevice(search)) return;
  const q = new URLSearchParams(search);
  const pick = (k: string) => q.get(k)?.trim().slice(0, 120) || null;
  const utm = landing
    ? { utm_source: pick('utm_source'), utm_medium: pick('utm_medium'), utm_campaign: pick('utm_campaign'), utm_content: pick('utm_content') }
    : { utm_source: null, utm_medium: null, utm_campaign: null, utm_content: null };
  const linkCode = landing ? pick('lk') : null;
  const referrer = landing ? document.referrer || null : null;
  const inApp = inAppBrowser();
  const sessionId = getSessionId();

  if (landing) {
    const source = classifySource(referrer, utm.utm_source, inApp);
    // Keep the last real source: a later direct visit doesn't erase where they came from.
    if (source !== 'Direct' || !getAttribution()) {
      const a: Attribution = {
        source,
        ...Object.fromEntries(Object.entries(utm).filter(([, v]) => v)),
        ...(linkCode ? { link_code: linkCode } : {}),
        landing_path: pathname,
        session_id: sessionId,
        site_key: SITE_KEY,
        at: new Date().toISOString(),
      };
      try {
        localStorage.setItem(ATTR_KEY, JSON.stringify(a));
      } catch {
        /* storage blocked — the visit is still counted */
      }
    }
  }

  // Fire-and-forget: the anon policy allows insert but not reading back, so no .select().
  supabase
    .from('site_traffic_events')
    .insert({
      site_key: SITE_KEY,
      session_id: sessionId,
      path: pathname,
      product_sku: pathname.match(/^\/products\/([^/]+)$/)?.[1] ?? null,
      seller_ref: null,
      referrer,
      landing,
      ...utm,
      link_code: linkCode,
      device: deviceType(),
      in_app: inApp,
    })
    .then(() => {});
}
